// Pure CSV assembly for the multi-part "Analyse" comparison download.
// Extracted from ComparisonView so the point-to-cell logic can be unit-tested without
// mounting the dialog (see CLAUDE.md "test pure logic" convention).

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

export const EXPORT_TIMEZONE = "Asia/Kolkata";

// The chart's axis labels carry no year: formatDateWithoutYear emits "09 Jul" for daily
// and weekly buckets, and monthly emits "Jul". Parsing either makes V8 resolve it to the
// year 2001, so they are passed through untouched instead.
//
// Matching these exact shapes matters. A looser "does it contain four digits" test also
// swallows raw mode's "14 Jul'26, 05:30:00 AM" - which has no four-digit run but is a
// real datetime that dayjs parses correctly - and returns it unconverted.
const YEARLESS_AXIS_LABEL = /^(\d{1,2} )?[A-Za-z]{3}$/;

export const escapeCSVValue = (value) => {
    if (value == null) return '';
    const str = String(value);
    if (/[",\n]/.test(str)) {
        return '"' + str.replace(/"/g, '""') + '"';
    }
    return str;
};

// In min-avg-max mode every node yields TWO series: a line carrying the average, and a
// rangeArea named "<node> mix" carrying [min, max]. Only the rangeArea point has min/max
// on it, and it is the one that can render all three values.
export const isRangePoint = (point) =>
    !!point &&
    typeof point === 'object' &&
    (point.min !== undefined || point.max !== undefined);

/**
 * One metric cell. A min-avg-max point renders as "min,avg,max"; anything else renders
 * its single value.
 *
 * The comma-joined triple is quoted by escapeCSVValue, so it stays one spreadsheet cell.
 */
export const formatMetricCell = (point) => {
    if (point == null) return '';
    if (typeof point !== 'object') return point;

    if (isRangePoint(point)) {
        return [point.min, point.avg, point.max]
            .map((value) => (value == null ? '' : value))
            .join(',');
    }

    const value = 'y' in point ? point.y : point;
    if (Array.isArray(value)) return value.join(',');
    return value ?? '';
};

/**
 * The timestamp for a point's row.
 *
 * Granularity points carry bucketStart, the bucket's real calendar date. It is used
 * verbatim: a daily/weekly/monthly bucket is a calendar day, not an instant, so
 * timezone-converting it would move the day for viewers outside the org's zone.
 *
 * Raw points have no bucketStart and their x IS a real datetime, so the zone conversion
 * applies there. A year-less value is passed through untouched rather than parsed, which
 * is what previously stamped every aggregated row with the year 2001.
 */
export const resolvePointTimestamp = (point, timezoneName = EXPORT_TIMEZONE) => {
    const bucketStart = point?.bucketStart;
    if (bucketStart) {
        const asString = String(bucketStart);
        const calendarDay = asString.match(/^\d{4}-\d{2}(-\d{2})?/);
        return calendarDay ? calendarDay[0] : asString;
    }

    const raw = point?.x;
    if (!raw) return '';

    const asString = String(raw);
    if (YEARLESS_AXIS_LABEL.test(asString)) return asString;

    const parsed = dayjs(raw).tz(timezoneName);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm:ss") : asString;
};

// Series are named "<node label> : <displayId>", and the rangeArea companion appends
// " mix". Both must reduce to the same node label so their points share a row.
export const getSeriesNodeLabel = (seriesName) => {
    const rawName = seriesName || '';
    return rawName.includes(' : ') ? rawName.split(' : ')[0] : rawName;
};

const buildNodePathMap = (nodes, path = {}, target = new Map()) => {
    (nodes || []).forEach((node) => {
        const level = node.level?.toLowerCase();
        const label = node.label || node.displayId || '';
        const nextPath = { ...path };
        if (level === 'string') {
            nextPath.stringNumber = label;
        } else if (level === 'pack') {
            nextPath.packNumber = label;
        } else if (level === 'module') {
            nextPath.moduleNumber = label;
        } else if (level === 'cell') {
            nextPath.cellNumber = label;
        }
        target.set(node.id, nextPath);
        if (node.children?.length) {
            buildNodePathMap(node.children, nextPath, target);
        }
    });
    return target;
};

const buildNodeInfoByLabel = (selectedStringNodes, nodePathById) => {
    const nodeInfoByLabel = new Map();
    (selectedStringNodes || []).forEach((node) => {
        const nodeLabel = node.label || node.displayId;
        if (!nodeLabel) return;

        const basePath = nodePathById.get(node.id) || {};
        const info = {
            stringNumber: basePath.stringNumber || '',
            packNumber: basePath.packNumber || '',
            moduleNumber: basePath.moduleNumber || '',
            cellNumber: basePath.cellNumber || '',
        };
        const level = node.level?.toLowerCase();
        if (level === 'string') {
            info.stringNumber = nodeLabel;
        } else if (level === 'pack') {
            info.packNumber = nodeLabel;
        } else if (level === 'module') {
            info.moduleNumber = nodeLabel;
        } else if (level === 'cell') {
            info.cellNumber = nodeLabel;
        }
        nodeInfoByLabel.set(nodeLabel, info);
        if (node.displayId) {
            nodeInfoByLabel.set(node.displayId, info);
        }
    });
    return nodeInfoByLabel;
};

const getMetricColumnLabel = (metric) =>
    metric?.label || metric?.name || metric?.key || "Metric";

/**
 * Build the comparison CSV.
 *
 * Rows are keyed by (timestamp, node), so the line series and its rangeArea companion
 * land on the same row. Where they collide the rangeArea point wins, because it is the
 * only one carrying all of min, avg and max - previously whichever series the loop
 * reached last simply overwrote the other, which silently dropped every average.
 */
export const buildComparisonCSV = ({
    metricsSeries = {},
    selectedMetrics = [],
    selectedNodes = [],
    batteryStructure = [],
    batteryInfo = {},
    vehicleInfo = {},
    maxMetrics = 4,
    timezoneName = EXPORT_TIMEZONE,
} = {}) => {
    const metricColumns = (selectedMetrics || []).slice(0, maxMetrics);
    const headerColumns = [
        "IMEI",
        "LicensePlateNo",
        "Timestamp",
        "stringNumber",
        "packNumber",
        "moduleNumber",
        "cellNumber",
        ...metricColumns.map(getMetricColumnLabel),
        "originDepot",
        "originCity",
        "originState",
    ];

    const nodePathById = buildNodePathMap(batteryStructure || []);
    const nodeInfoByLabel = buildNodeInfoByLabel(selectedNodes, nodePathById);

    const rowsMap = {};
    // Tracks which cells already hold a min-avg-max triple, so a companion line point
    // cannot overwrite one with a bare average regardless of series order.
    const rangeFilledCells = new Set();

    metricColumns.forEach((metric) => {
        const columnLabel = getMetricColumnLabel(metric);
        const seriesList = metricsSeries?.[metric.key] || [];

        seriesList.forEach((series) => {
            const nodeLabel = getSeriesNodeLabel(series?.name);

            (series.data || []).forEach((point) => {
                const timestamp = resolvePointTimestamp(point, timezoneName);
                if (!timestamp) return;

                const rowKey = `${timestamp}||${nodeLabel}`;
                if (!rowsMap[rowKey]) {
                    const hierarchyInfo = nodeInfoByLabel.get(nodeLabel) || {};
                    rowsMap[rowKey] = {
                        IMEI: batteryInfo?.deviceID || '',
                        LicensePlateNo: vehicleInfo?.licensePlateNo || '',
                        Timestamp: timestamp,
                        stringNumber: hierarchyInfo.stringNumber || nodeLabel || '',
                        packNumber: hierarchyInfo.packNumber || '',
                        moduleNumber: hierarchyInfo.moduleNumber || '',
                        cellNumber: hierarchyInfo.cellNumber || '',
                        originDepot: vehicleInfo?.originDepo || vehicleInfo?.originDepot || '',
                        originCity: vehicleInfo?.originCity || '',
                        originState: vehicleInfo?.originState || '',
                    };
                }

                const cellKey = `${rowKey}||${columnLabel}`;
                if (rangeFilledCells.has(cellKey)) return;

                rowsMap[rowKey][columnLabel] = formatMetricCell(point);
                if (isRangePoint(point)) rangeFilledCells.add(cellKey);
            });
        });
    });

    const dataRows = Object.values(rowsMap);
    const headerLine = headerColumns.map(escapeCSVValue).join(',');
    const rowLines = dataRows.map((row) => (
        headerColumns.map((column) => escapeCSVValue(row[column] ?? '')).join(',')
    ));

    return [headerLine, ...rowLines].join('\n');
};
